import React, { useCallback, useEffect, useRef, useState } from 'react';
import T from 'i18n';
import {
  Add,
  AutoAwesome,
  IntegrationInstructions,
  IosShare,
  KeyboardArrowDown,
  ManageHistory,
  PostAdd,
  Undo,
} from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
} from '@mui/material';
import { useToasts } from 'react-toast-notifications';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAiToolsFilters,
  selectAskAiSseHasResponseArea,
  selectAskAiSearchType,
  selectCurrentCaseId,
  selectDocuments,
  selectFolders,
  selectUserId,
  selectUserSettings,
} from 'common/selectors';
import api from 'common/api';
import history from 'common/history';
import OutputFormatMenu from 'features/case/SearchCenter/OutputFormatMenu';
import AiToolsDialog from 'features/case/SearchCenter/AiToolsDialog';
import FilesAndBundlesSelectionDialog from 'features/case/SearchCenter/FilesAndBundlesSelectionDialog';
import { getSelectedLocation } from 'features/case/SearchCenter/SearchInputLocations';
import SearchOptions from 'features/case/SearchCenter/SearchOptions';
import SearchHistory from 'features/case/SearchCenter/SearchHistory';
import type { FileItem } from 'features/case/SearchCenter/FilesSelectionPanel';
import {
  askAiSse,
  cancelAskAiSse,
  resetAskAiSse,
  setAskAiSseSelectedBundles,
  setAskAiSseSelectedFiles,
} from 'features/case/redux/askAiSse';
import { setAiToolsFilters } from 'features/case/redux/setAiToolsFilters';
import { fetchAskAiSseChatMessages } from 'features/case/redux/fetchAskAiSseChatMessages';
import ChatStream from './components/ChatStream';
import PromptComposer from './components/PromptComposer';
import { AI_TOOLS_MENU_ITEMS, CLOSE_DELAY_MS, DEFAULT_RESPONSE_MODE } from './constants';
import type { ResponseMode } from './constants';
import { getOutputFormatLabel, truncateName } from './utils/labels';
import type { PromptCategorySummary } from './utils/promptLibrarySave';
import { savePromptToLibrary, sanitizePromptCategories } from './utils/promptLibrarySave';
import { normalizeAskAiSseVersion, supportsAskAiSseResponseMode } from './utils/version';
import { useChatRouteState } from './hooks/useChatRouteState';
import { useAutoScroll } from './hooks/useAutoScroll';
import { useChatHydration } from './hooks/useChatHydration';
import { useCitationNavigation } from './hooks/useCitationNavigation';
import { useChatList } from './hooks/useChatList';
import { useBundleFilesCache } from './hooks/useBundleFilesCache';
import {
  buildStandingInstructionsPrompt,
  hasStandingInstructions,
  normalizeStandingInstructions,
} from 'features/case/utils/standingInstructions';
import { useUpdateUserSettings } from 'features/common/redux/updateUserSettings';
import styles from './AskAISSE.module.scss';

type AskAiSSEPageProps = {
  fileSidebarAskAI?: boolean;
  fileData?: any;
  focus?: AskAiSseFocus | null;
};

type ImprovePromptResponse = {
  originalPrompt?: string;
  improvedPrompt?: string;
  model?: string;
  goal?: string;
  error?: {
    message?: string;
  };
};

type AskAiSseFocus = { type: 'file'; file: string } | { type: 'bundle'; bundle: string };

const findFolderById = (folders: any, targetId: string | number): any | null => {
  const normalizedTarget = String(targetId);
  if (!Array.isArray(folders)) return null;
  for (const folder of folders) {
    if (!folder || typeof folder !== 'object') continue;
    if (String(folder.id) === normalizedTarget) return folder;
    const child = findFolderById(folder.folders, normalizedTarget);
    if (child) return child;
  }
  return null;
};

export default function AskAiSSEPage({
  fileSidebarAskAI = false,
  fileData,
  focus,
}: AskAiSSEPageProps) {
  const { addToast } = useToasts();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigationState = (location as any)?.state as Record<string, any> | undefined;
  const skipAskAiHydration = Boolean(navigationState?.skipAskAiHydration);
  const skipAskAiReset = Boolean(navigationState?.skipAskAiReset);
  const aiToolsFiltersFromState = useSelector(selectAiToolsFilters) as any;
  const caseId = useSelector(selectCurrentCaseId);
  const userId = useSelector(selectUserId);
  const documents = useSelector(selectDocuments) as Array<any>;
  const foldersByType = useSelector(selectFolders) as any;
  const userSettings = useSelector(selectUserSettings) as any;
  const { updateUserSettings } = useUpdateUserSettings();
  const askAiVersion = normalizeAskAiSseVersion(userSettings?.aiSettings?.version);
  const standingInstructions = React.useMemo(
    () => normalizeStandingInstructions(userSettings?.aiSettings?.standingInstructions),
    [userSettings?.aiSettings?.standingInstructions],
  );
  const hasSavedStandingInstructions = hasStandingInstructions(standingInstructions);
  const supportsResponseMode = supportsAskAiSseResponseMode(askAiVersion);
  const askAiSseState = useSelector((state: any) => state.case || {});
  const showResponseArea = useSelector(selectAskAiSseHasResponseArea);
  const askAiSearchType = useSelector(selectAskAiSearchType);
  const isAiToolsRoute = (location?.pathname || '').includes('/ai-tools');
  const askAiBundleFromUrl = React.useMemo(() => {
    const search = location?.search || '';
    if (!search) return null;
    const params = new URLSearchParams(search);
    const raw = params.get('askAiBundle') || params.get('askAIBundle');
    if (!raw) return null;
    const trimmed = String(raw).trim();
    return trimmed.length > 0 ? trimmed : null;
  }, [location?.search]);
  const shouldHideQuickActionsMenuTrigger = React.useMemo(() => {
    if (isAiToolsRoute) return false;
    const search = location?.search || '';
    if (!search) return false;
    return new URLSearchParams(search).get('askAI') === 'true';
  }, [isAiToolsRoute, location?.search]);
  const shouldAlignAiToolsLeft = !fileSidebarAskAI && isAiToolsRoute;
  const showShareButton = false; // TODO: enable once the share backend is ready.
  const {
    askAiSseStatus,
    askAiSseAssistantText,
    askAiSseProgressMessage,
    askAiSseError,
    askAiSseHasFirstChunk,
    askAiSseHistory,
    askAiSseCurrentId,
    askAiSseCitationsBySource,
    askAiSseSelectedBundles,
    askAiSseSelectedFiles,
    askAiSseSelectedFilesType,
    askAiSseChatId,
    askAiSseChatMessagesResponse,
    fetchAskAiSseChatMessagesPending,
    askAiSseResetCount,
  } = askAiSseState;
  const [inputValue, setInputValue] = useState('');
  const [selectedOutputFormat, setSelectedOutputFormat] = useState<string | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const streamEndRef = useRef<HTMLDivElement>(null);
  const [answerFormatAnchor, setAnswerFormatAnchor] = useState<HTMLElement | null>(null);
  const [answerFormatMenuOpen, setAnswerFormatMenuOpen] = useState(false);
  const [aiToolsDialogOpen, setAiToolsDialogOpen] = useState(false);
  const [filesDialogOpen, setFilesDialogOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileItem[]>([]);
  const [compareFilesMode, setCompareFilesMode] = useState(false);
  const [compareFiles, setCompareFiles] = useState<FileItem[]>([]);
  const bundlesAnchorRef = useRef<HTMLSpanElement>(null);
  const [searchOptionsAnchorEl, setSearchOptionsAnchorEl] = useState<HTMLElement | null>(null);
  const isOverAnswerFormatItemRef = useRef(false);
  const isOverOutputFormatMenuRef = useRef(false);
  const [responseMode, setResponseMode] = useState<ResponseMode>(DEFAULT_RESPONSE_MODE);
  const useStandingInstructionsForPrompt = Boolean(standingInstructions?.enabled);
  const [selectedPromptCategoryName, setSelectedPromptCategoryName] = useState<string>();
  const [selectedPromptName, setSelectedPromptName] = useState<string>();
  const [promptCategoriesCache, setPromptCategoriesCache] = useState<PromptCategorySummary[]>([]);
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [isImprovingPrompt, setIsImprovingPrompt] = useState(false);
  const [resetPromptOriginalText, setResetPromptOriginalText] = useState('');
  const [historyMenuAnchorEl, setHistoryMenuAnchorEl] = useState<HTMLElement | null>(null);
  const historyMenuOpen = Boolean(historyMenuAnchorEl);
  const fileId = fileSidebarAskAI ? (fileData?.id ?? fileData?.fileId) : null;
  const appliedBundleSelectionRef = useRef<string | null>(null);
  const effectiveFocus = React.useMemo<AskAiSseFocus | null>(() => {
    if (!fileSidebarAskAI) return null;
    const hasExplicitFocus = typeof focus !== 'undefined';
    if (hasExplicitFocus) {
      if (focus?.type === 'bundle' && focus?.bundle) {
        return { type: 'bundle', bundle: String(focus.bundle) };
      }
      if (focus?.type === 'file' && focus?.file) {
        return { type: 'file', file: String(focus.file) };
      }
      return null;
    }
    if (!fileId) return null;
    return { type: 'file', file: String(fileId) };
  }, [fileSidebarAskAI, focus, fileId]);
  const focusFileId =
    fileSidebarAskAI && effectiveFocus && effectiveFocus.type === 'file'
      ? effectiveFocus.file
      : null;
  const chatListFocus = effectiveFocus;
  const historyMenuAnchorOrigin = fileSidebarAskAI
    ? { vertical: 'bottom', horizontal: 'right' }
    : { vertical: 'bottom', horizontal: 'left' };
  const historyMenuTransformOrigin = fileSidebarAskAI
    ? { vertical: 'top', horizontal: 'right' }
    : { vertical: 'top', horizontal: 'left' };

  const { chatIdFromUrl: rawChatIdFromUrl, isFileOpen } = useChatRouteState(location?.pathname);
  const shouldSkipHydration = skipAskAiHydration;
  const shouldSkipReset = shouldSkipHydration || skipAskAiReset;
  const chatIdFromUrl = fileSidebarAskAI ? null : rawChatIdFromUrl;
  const effectiveChatId = fileSidebarAskAI ? askAiSseChatId : chatIdFromUrl || askAiSseChatId;
  const { resetHydrationGuard } = useChatHydration({
    userId,
    caseId,
    chatIdFromUrl,
    isFileOpen,
    fileSidebarAskAI,
    skipHydration: shouldSkipHydration,
  });
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shouldRefreshOnCompleteRef = useRef(false);
  const baseAiToolsInitRef = useRef(false);
  const isMountedRef = useRef(true);
  const [chatListRefreshPending, setChatListRefreshPending] = useState(false);
  const selectedBundles = React.useMemo(
    () => (Array.isArray(askAiSseSelectedBundles) ? askAiSseSelectedBundles : []),
    [askAiSseSelectedBundles],
  );
  useBundleFilesCache({
    caseId,
    selectedBundles,
  });
  const aiToolsFilters = React.useMemo(
    () => ({
      files: selectedFiles,
      location: selectedBundles,
      compareFiles: compareFiles,
      isCompareMode: compareFiles.length > 0,
      promptCategory: selectedPromptCategoryName,
      promptName: selectedPromptName,
    }),
    [selectedFiles, selectedBundles, compareFiles, selectedPromptCategoryName, selectedPromptName],
  );
  const visibleQuickActionMenuItems = React.useMemo(
    () =>
      AI_TOOLS_MENU_ITEMS.filter(
        (menuItem) => !['savePrompt', 'options'].includes(menuItem.key) || isAiToolsRoute,
      ),
    [isAiToolsRoute],
  );
  const savePromptMenuItem = React.useMemo(
    () =>
      isAiToolsRoute
        ? AI_TOOLS_MENU_ITEMS.find((menuItem) => menuItem.key === 'savePrompt') || null
        : null,
    [isAiToolsRoute],
  );
  const menuOpen = Boolean(menuAnchorEl);
  const safeHistory = askAiSseHistory || [];
  const isStreaming = askAiSseStatus === 'loading' || askAiSseStatus === 'streaming';
  const showPromptLoader =
    Boolean(chatIdFromUrl) &&
    !fetchAskAiSseChatMessagesPending &&
    !askAiSseChatMessagesResponse &&
    safeHistory.length === 0 &&
    !shouldSkipReset;
  const hidePromptComposer =
    fetchAskAiSseChatMessagesPending && (Boolean(chatIdFromUrl) || fileSidebarAskAI);
  const autoScrollContentVersion = `${askAiSseCurrentId || 'none'}:${askAiSseStatus}:${
    safeHistory.length
  }:${String(askAiSseAssistantText || '').length}:${askAiSseProgressMessage || ''}:${
    askAiSseError || ''
  }`;

  const getDocumentsChipLabel = React.useMemo(() => {
    if (selectedFiles && selectedFiles.length > 0) {
      const first = selectedFiles[0];
      const firstName = first?.name || first?.id || '';
      const label = truncateName(firstName, 15);
      return selectedFiles.length > 1 ? `${label}, …` : label;
    }
    if (selectedBundles && selectedBundles.length > 0) {
      const first = selectedBundles[0];
      const firstName = first?.name || first?.id || '';
      const label = truncateName(firstName, 15);
      return selectedBundles.length > 1 ? `${label}, …` : label;
    }
    return '';
  }, [selectedFiles, selectedBundles]);

  const getCompareChipLabel = React.useMemo(() => {
    if (!compareFiles || compareFiles.length === 0) return '';
    const first = compareFiles[0];
    const firstName = first?.name || first?.id || '';
    const label = truncateName(firstName, 15);
    return compareFiles.length > 1 ? `${label}, …` : label;
  }, [compareFiles]);

  useEffect(() => {
    if (!menuOpen) {
      setAnswerFormatMenuOpen(false);
      setAnswerFormatAnchor(null);
    }
  }, [menuOpen]);

  const handleStandingInstructionsToggle = useCallback(
    async (checked: boolean) => {
      if (!standingInstructions) return;
      try {
        await updateUserSettings({
          userSettings: {
            ...userSettings,
            aiSettings: {
              ...(userSettings?.aiSettings || {}),
              standingInstructions: {
                ...standingInstructions,
                enabled: checked,
              },
            },
          },
        });
      } catch (error) {
        console.error('Error updating standing instructions toggle:', error);
      }
    },
    [standingInstructions, updateUserSettings, userSettings],
  );

  useEffect(
    () => () => {
      dispatch(cancelAskAiSse());
    },
    [dispatch],
  );

  // chat hydration is handled by useChatHydration

  const { scrollContainerRef, showJumpToLatest, handleScroll, jumpToLatest } = useAutoScroll({
    streamEndRef,
    activeRequestId: askAiSseCurrentId,
    isStreaming,
    contentVersion: autoScrollContentVersion,
    showResponseArea,
  });

  const handleMenuToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isSavingPrompt) return;
    if (menuOpen) {
      setMenuAnchorEl(null);
      handleAnswerFormatMenuClose();
      return;
    }
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    handleAnswerFormatMenuClose();
  };

  const resetChatListRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    shouldRefreshOnCompleteRef.current = false;
    if (isMountedRef.current) {
      setChatListRefreshPending(false);
    }
  }, []);

  const dispatchAskAiSse = (prompt: string, options: { clearInput?: boolean } = {}) => {
    const rawPrompt = String(prompt ?? '').trim();
    if (!rawPrompt || !caseId || !userId) return;
    if (isStreaming) return;
    const wrappedPrompt = buildStandingInstructionsPrompt(
      rawPrompt,
      standingInstructions,
      useStandingInstructionsForPrompt,
    );
    if (!wrappedPrompt) return;
    const version = askAiVersion;
    const includeIds = (selectedFiles || [])
      .map((f) => f?.id)
      .filter(Boolean)
      .map(String);
    const bundleIds = (selectedBundles || [])
      .map((b) => b?.id)
      .filter(Boolean)
      .map(String);
    const compareIds = (compareFiles || [])
      .map((f) => f?.id)
      .filter(Boolean)
      .map(String);
    // For file sidebar comparisons, force the pivot to be the focused file.
    const pivotFileId = fileSidebarAskAI && focusFileId ? String(focusFileId) : '';
    const comparePayloadIds =
      pivotFileId && compareIds.length > 0
        ? [pivotFileId, ...compareIds.filter((id) => id !== pivotFileId)]
        : compareIds;

    // Use chatId from URL first, fallback to Redux store
    const chatId = effectiveChatId;
    resetChatListRefresh();
    shouldRefreshOnCompleteRef.current = !chatId;

    dispatch(
      askAiSse({
        caseId,
        userId,
        input: wrappedPrompt,
        displayPrompt: wrappedPrompt,
        version,
        ...(selectedOutputFormat ? { outputFormat: selectedOutputFormat } : {}),
        ...(includeIds.length > 0 ? { include: includeIds } : {}),
        ...(bundleIds.length > 0 ? { bundles: bundleIds } : {}),
        ...(comparePayloadIds.length > 0 ? { compare: comparePayloadIds } : {}),
        ...(chatId ? { chatId } : {}),
        ...(effectiveFocus ? { focus: effectiveFocus } : {}),
        ...(supportsResponseMode && responseMode === 'fast' ? { modelParameter: 'fast' } : {}),
        ...(fileSidebarAskAI ? { suppressUrlUpdate: true } : {}),
      }),
    );
    if (options.clearInput) {
      setInputValue('');
      setResetPromptOriginalText('');
      setSelectedPromptCategoryName(undefined);
      setSelectedPromptName(undefined);
    }
  };

  const sendMessage = () => {
    dispatchAskAiSse(inputValue, { clearInput: true });
  };

  const handleStopStreaming = useCallback(() => {
    dispatch(cancelAskAiSse());
  }, [dispatch]);

  const handleRestartChat = (prompt: string) => {
    dispatchAskAiSse(prompt);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    sendMessage();
  };

  const handleHistoryMenuToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (historyMenuOpen) {
      setHistoryMenuAnchorEl(null);
      return;
    }
    setHistoryMenuAnchorEl(event.currentTarget);
  };

  const handleHistoryMenuClose = () => {
    setHistoryMenuAnchorEl(null);
  };

  const handleChatHistoryItemClick = (selected: any) => {
    const chatId = selected?.id || selected?.chatId;
    handleHistoryMenuClose();
    if (!chatId) return;
    if (fileSidebarAskAI) {
      if (!userId || !caseId) return;
      dispatch(cancelAskAiSse());
      dispatch(fetchAskAiSseChatMessages({ userId, caseId, chatId: String(chatId) })).catch(
        (error: any) => {
          const msg = error?.detail || error?.message || 'Chat not found';
          addToast(msg, { appearance: 'error', autoDismiss: true });
        },
      );
      return;
    }
    const base = history.location.pathname.replace(/\/ai-tools.*/, ``);
    history.push(`${base}/ai-tools/${chatId}`);
  };

  const { handleCitationClick, resetCitationCache } = useCitationNavigation({
    caseId,
    documents,
    effectiveChatId,
    fileSidebarAskAI,
  });

  const resetToFreshSession = useCallback(
    (options: { resetHydrationGuard?: boolean } = {}) => {
      const shouldResetHydrationGuard = options.resetHydrationGuard !== false;

      // Stop any in-flight streaming and fully reset UI + store back to defaults.
      dispatch(cancelAskAiSse());
      dispatch(resetAskAiSse());

      // Local component state
      setInputValue('');
      setResetPromptOriginalText('');
      setSelectedOutputFormat(null);
      setSelectedFiles([]);
      setCompareFilesMode(false);
      setCompareFiles([]);
      setMenuAnchorEl(null);
      setAnswerFormatMenuOpen(false);
      setAnswerFormatAnchor(null);
      setAiToolsDialogOpen(false);
      setSelectedPromptCategoryName(undefined);
      setSelectedPromptName(undefined);
      setIsSavingPrompt(false);
      setFilesDialogOpen(false);
      setSearchOptionsAnchorEl(null);

      // Internal guards/caches that would otherwise make this feel "not fresh"
      if (shouldResetHydrationGuard) {
        resetHydrationGuard();
      }
      resetCitationCache();
    },
    [dispatch, resetCitationCache, resetHydrationGuard],
  );

  useEffect(() => {
    if (shouldSkipReset) return;
    resetToFreshSession({ resetHydrationGuard: !chatIdFromUrl });
  }, [askAiSseResetCount, chatIdFromUrl, resetToFreshSession, shouldSkipReset]);

  useEffect(() => {
    if (!askAiSseSelectedFilesType) return;
    const nextFiles = Array.isArray(askAiSseSelectedFiles)
      ? (askAiSseSelectedFiles as FileItem[])
      : [];
    if (askAiSseSelectedFilesType === 'compareFiles') {
      setCompareFiles(nextFiles);
      setSelectedFiles([]);
    } else {
      setSelectedFiles(nextFiles);
      setCompareFiles([]);
    }
    dispatch(setAskAiSseSelectedFiles({ files: [], selectionType: null }));
  }, [askAiSseSelectedFiles, askAiSseSelectedFilesType, dispatch]);

  const {
    groupedChatHistory,
    loading: chatListLoading,
    refreshing: chatListRefreshing,
    refreshChatList,
    renameChat: handleRenameChat,
    deleteChat: handleDeleteChat,
  } = useChatList({
    userId,
    caseId,
    effectiveChatId,
    focus: chatListFocus,
    onDeletedCurrent: () => {
      resetToFreshSession();
      if (fileSidebarAskAI) return;
      const base = history.location.pathname.replace(/\/ai-tools.*/, ``);
      history.push(`${base}/ai-tools`);
    },
  });

  useEffect(() => {
    if (askAiSseStatus !== 'complete') return;
    if (!shouldRefreshOnCompleteRef.current) return;
    shouldRefreshOnCompleteRef.current = false;
    setChatListRefreshPending(true);
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    refreshTimerRef.current = setTimeout(() => {
      Promise.resolve(refreshChatList()).finally(() => {
        if (!isMountedRef.current) return;
        setChatListRefreshPending(false);
      });
    }, 300);
  }, [askAiSseStatus, refreshChatList]);

  useEffect(() => {
    if (askAiSseStatus === 'error' || askAiSseStatus === 'idle') {
      resetChatListRefresh();
    }
  }, [askAiSseStatus, resetChatListRefresh]);

  useEffect(() => {
    if (shouldHideQuickActionsMenuTrigger) {
      setMenuAnchorEl(null);
    }
  }, [shouldHideQuickActionsMenuTrigger]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      shouldRefreshOnCompleteRef.current = false;
    };
  }, []);

  const handleNewSearch = () => {
    resetToFreshSession();
    if (fileSidebarAskAI) return;
    // Navigate to base ai-tools route (remove chatId and any deeper segments like /files/:file)
    const base = history.location.pathname.replace(/\/ai-tools.*/, ``);
    history.push(`${base}/ai-tools`);
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    addToast(T.translate('generic.copytoClipboard') || 'Copied to clipboard!', {
      appearance: 'success',
      autoDismiss: true,
    });
  };

  // Treat base /ai-tools (no chatId) as a brand-new chat every time we land on it.
  // This also covers hard refresh on /ai-tools.
  useEffect(() => {
    const pathname = location?.pathname || '';
    const isBaseAiTools = !chatIdFromUrl && /\/ai-tools\/?$/.test(pathname);

    // If we're not on base ai-tools, allow init to run again the next time we return.
    if (!isBaseAiTools) {
      baseAiToolsInitRef.current = false;
      return;
    }
    if (shouldSkipReset) return;
    if (baseAiToolsInitRef.current) return;
    baseAiToolsInitRef.current = true;

    resetToFreshSession();
  }, [location?.pathname, chatIdFromUrl, resetToFreshSession, shouldSkipReset]);

  useEffect(() => {
    if (!askAiBundleFromUrl || fileSidebarAskAI || !isAiToolsRoute) return;
    const selectionKey = `${askAiBundleFromUrl}-${askAiSseResetCount || 0}`;

    const bundleId = String(askAiBundleFromUrl).trim();
    if (!bundleId) return;

    const isBundleSelected = selectedBundles.some((bundle) => String(bundle?.id) === bundleId);
    if (isBundleSelected) {
      appliedBundleSelectionRef.current = selectionKey;
      return;
    }
    if (appliedBundleSelectionRef.current === selectionKey) {
      appliedBundleSelectionRef.current = null;
    }

    const folders = foldersByType || {};
    const folderRoots = Object.values(folders).filter(Array.isArray);
    let folderFromState: any | null = null;
    for (const root of folderRoots) {
      folderFromState = findFolderById(root, bundleId);
      if (folderFromState) break;
    }
    const filterFolder = aiToolsFiltersFromState?.folder;
    const filterFolderId = filterFolder?.id != null ? String(filterFolder.id) : null;
    const resolvedFolder = folderFromState || (filterFolderId === bundleId ? filterFolder : null);
    if (!resolvedFolder) return;

    dispatch(setAskAiSseSelectedBundles(getSelectedLocation(resolvedFolder)));
    appliedBundleSelectionRef.current = selectionKey;
  }, [
    aiToolsFiltersFromState?.folder,
    askAiBundleFromUrl,
    askAiSseResetCount,
    dispatch,
    fileSidebarAskAI,
    foldersByType,
    isAiToolsRoute,
    selectedBundles,
  ]);

  const fileIdToName = React.useMemo(() => {
    const map: Record<string, string> = {};
    (documents || []).forEach((doc: any) => {
      const id = doc?.id ?? doc?.fileId;
      const name = doc?.name ?? doc?.fileName ?? doc?.filename;
      if (id && name) map[String(id)] = String(name);
    });
    return map;
  }, [documents]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleAnswerFormatClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      if (!isOverAnswerFormatItemRef.current && !isOverOutputFormatMenuRef.current) {
        handleAnswerFormatMenuClose();
      }
    }, CLOSE_DELAY_MS);
  };

  const handleAnswerFormatMenuClose = () => {
    isOverAnswerFormatItemRef.current = false;
    isOverOutputFormatMenuRef.current = false;
    clearCloseTimer();
    setAnswerFormatMenuOpen(false);
    setAnswerFormatAnchor(null);
  };

  const handleOutputFormatMenuEnter = () => {
    isOverOutputFormatMenuRef.current = true;
    clearCloseTimer();
  };

  const handleOutputFormatMenuLeave = () => {
    isOverOutputFormatMenuRef.current = false;
    scheduleAnswerFormatClose();
  };

  const handleNonAnswerMenuEnter = () => {
    handleAnswerFormatMenuClose();
  };

  const handleAnswerFormatSelect = (format: string) => {
    setSelectedOutputFormat(format);
    handleAnswerFormatMenuClose();
    handleMenuClose();
  };

  const handleOptionsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSearchOptionsAnchorEl(event.currentTarget);
    handleMenuClose();
  };

  const handleClearOutputFormat = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedOutputFormat(null);
  };

  const handleClearSelectedFiles = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedFiles([]);
    clearBundleSelection();
  };

  const clearAskAiBundleParam = () => {
    if (fileSidebarAskAI) return;
    const search = location?.search || '';
    if (!search) return;

    const params = new URLSearchParams(search);
    if (!params.has('askAiBundle') && !params.has('askAIBundle')) return;

    params.delete('askAiBundle');
    params.delete('askAIBundle');
    const nextSearch = params.toString();
    const nextPath = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${
      location.hash || ''
    }`;
    const currentState = (location as any)?.state;
    const baseState =
      currentState && typeof currentState === 'object' ? (currentState as Record<string, any>) : {};
    history.replace(nextPath, { ...baseState, skipAskAiReset: true });
  };

  const clearBundleSelection = () => {
    const nextFilters =
      aiToolsFiltersFromState && typeof aiToolsFiltersFromState === 'object'
        ? { ...aiToolsFiltersFromState }
        : {};
    if ('folder' in nextFilters) {
      delete nextFilters.folder;
    }
    dispatch(setAiToolsFilters(nextFilters));
    dispatch(setAskAiSseSelectedBundles([]));
    appliedBundleSelectionRef.current = null;
    clearAskAiBundleParam();
  };

  const handleClearCompareFiles = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setCompareFiles([]);
  };

  const handlePromptCategoriesLoaded = useCallback((categories: PromptCategorySummary[]) => {
    setPromptCategoriesCache(sanitizePromptCategories(categories));
  }, []);

  const handleInputValueChange = useCallback((nextValue: string) => {
    setInputValue(nextValue);
    if (!String(nextValue).trim()) {
      setSelectedPromptCategoryName(undefined);
      setSelectedPromptName(undefined);
    }
  }, []);

  const requestImprovedPrompt = useCallback(
    async (prompt: string): Promise<{ improvedPrompt: string; originalPrompt: string } | null> => {
      try {
        const res = (await api.post('/prompts/improve', {
          prompt: prompt.trim(),
        })) as ImprovePromptResponse;

        if (res?.error) {
          addToast(res.error.message || 'Failed to improve prompt. Please try again.', {
            appearance: 'error',
            autoDismiss: true,
          });
          return null;
        }

        const improvedPrompt = typeof res?.improvedPrompt === 'string' ? res.improvedPrompt : '';
        if (!improvedPrompt.trim()) {
          addToast('Failed to improve prompt. Please try again.', {
            appearance: 'error',
            autoDismiss: true,
          });
          return null;
        }

        const originalPrompt = typeof res?.originalPrompt === 'string' ? res.originalPrompt : '';

        return {
          improvedPrompt,
          originalPrompt,
        };
      } catch (error) {
        console.error('Error improving prompt:', error);
        addToast('Failed to improve prompt. Please try again.', {
          appearance: 'error',
          autoDismiss: true,
        });
        return null;
      }
    },
    [addToast],
  );

  const handleEnhancePrompt = useCallback(async () => {
    if (!inputValue.trim() || isImprovingPrompt) return;

    setIsImprovingPrompt(true);

    try {
      const result = await requestImprovedPrompt(inputValue);
      if (!result) return;
      setInputValue(result.improvedPrompt);
      setResetPromptOriginalText(result.originalPrompt);
      handleMenuClose();
    } finally {
      setIsImprovingPrompt(false);
    }
  }, [handleMenuClose, inputValue, isImprovingPrompt, requestImprovedPrompt]);

  const handleResetEnhancedPrompt = useCallback(() => {
    if (!resetPromptOriginalText) return;
    setInputValue(resetPromptOriginalText);
    setResetPromptOriginalText('');
    handleMenuClose();
  }, [handleMenuClose, resetPromptOriginalText]);

  const handleSavePrompt = useCallback(async () => {
    if (!inputValue.trim() || isSavingPrompt) return;

    setIsSavingPrompt(true);

    try {
      const result = await savePromptToLibrary({
        apiClient: api,
        promptText: inputValue,
        selectedPromptCategoryName,
        selectedPromptName,
        cachedCategories: promptCategoriesCache,
        uncategorizedName: T.translate('generic.uncategorized') || 'Uncategorized',
      });

      setPromptCategoriesCache(result.categories);
      isOverAnswerFormatItemRef.current = false;
      isOverOutputFormatMenuRef.current = false;
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setAnswerFormatMenuOpen(false);
      setAnswerFormatAnchor(null);
      setMenuAnchorEl(null);
      addToast(T.translate('generic.promptsSaved') || 'Prompts saved', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch {
      addToast(T.translate('generic.failedToSavePrompt') || 'Failed to save the prompt', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsSavingPrompt(false);
    }
  }, [
    addToast,
    inputValue,
    isSavingPrompt,
    promptCategoriesCache,
    selectedPromptCategoryName,
    selectedPromptName,
  ]);

  const handleOpenCompareDialog = () => {
    setCompareFilesMode(true);
    setFilesDialogOpen(true);
  };

  const handleOpenDocumentsDialog = () => {
    setCompareFilesMode(false);
    setFilesDialogOpen(true);
  };

  const handleAiToolsClose = () => {
    setAiToolsDialogOpen(false);
  };

  const handleFilesDialogClose = () => {
    setFilesDialogOpen(false);
  };

  const handleFilesAndBundlesSave = ({
    files,
    bundles,
  }: {
    files: FileItem[];
    bundles: { id: string; name: string }[];
  }) => {
    if (files.length > 0) {
      setSelectedFiles(files);
      setCompareFiles([]);
      clearBundleSelection();
      dispatch(setAskAiSseSelectedFiles({ files, selectionType: 'files' }));
    } else if (bundles.length > 0) {
      dispatch(setAskAiSseSelectedBundles(bundles));
      clearAskAiBundleParam();
      setSelectedFiles([]);
      setCompareFiles([]);
    } else {
      setSelectedFiles([]);
      clearBundleSelection();
    }
    setFilesDialogOpen(false);
  };
  return (
    <div
      className={`${styles.page} ${fileSidebarAskAI ? styles.pageFileSidebarAskAI : ''} ${
        shouldAlignAiToolsLeft ? styles.pageAiToolsAlignLeft : ''
      }`}
    >
      <div
        className={`${styles.columns} ${shouldAlignAiToolsLeft ? styles.columnsAiTools : ''} ${showResponseArea ? styles.columnsWithResponse : ''}`}
      >
        {!isFileOpen && !fileSidebarAskAI && (
          <div className={styles.sideColumn}>
            <Button
              type="button"
              variant="outlined"
              size="small"
              className={styles.sideButton}
              onClick={handleNewSearch}
            >
              <Add fontSize="small" />
              {T.translate('generic.newChat')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              size="small"
              className={`${styles.sideButton} ${historyMenuOpen ? styles.sideButtonOpen : ''}`}
              onClick={handleHistoryMenuToggle}
            >
              <KeyboardArrowDown
                fontSize="small"
                className={`${styles.arrowIcon} ${historyMenuOpen ? styles.arrowIconOpen : ''}`}
              />
              {T.translate('case.askAISearchHistory')}
            </Button>
          </div>
        )}

        {historyMenuOpen && historyMenuAnchorEl && (
          <SearchHistory
            open={historyMenuOpen}
            anchorEl={historyMenuAnchorEl}
            handleSearchHistoryClose={handleHistoryMenuClose}
            handleSearchHistoryMenuItemClick={handleChatHistoryItemClick}
            searchHistory={groupedChatHistory}
            useReduxSearchHistory={false}
            loading={chatListLoading}
            inlineLoading={chatListRefreshPending || chatListRefreshing}
            showActions={true}
            onRename={handleRenameChat}
            onDelete={handleDeleteChat}
            truncateTitles
            anchorOrigin={historyMenuAnchorOrigin}
            transformOrigin={historyMenuTransformOrigin}
          />
        )}

        <div
          className={`${styles.centerCol} ${showResponseArea ? styles.centerColStreaming : ''} ${
            fileSidebarAskAI && !showResponseArea ? styles.centerColFileSidebarTop : ''
          } ${shouldAlignAiToolsLeft ? styles.centerColAiTools : ''}`}
        >
          {/* Responsive inline buttons - shown via CSS when side columns are hidden */}
          {!isFileOpen && !fileSidebarAskAI && (
            <div className={styles.inlineButtonsRow}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  className={styles.sideButton}
                  onClick={handleNewSearch}
                  style={{ width: 'auto' }}
                >
                  <Add fontSize="small" />
                  {T.translate('generic.newChat')}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  className={`${styles.sideButton} ${historyMenuOpen ? styles.sideButtonOpen : ''}`}
                  onClick={handleHistoryMenuToggle}
                  style={{ width: 'auto' }}
                >
                  <KeyboardArrowDown
                    fontSize="small"
                    className={`${styles.arrowIcon} ${historyMenuOpen ? styles.arrowIconOpen : ''}`}
                  />
                  {T.translate('case.askAISearchHistory')}
                </Button>
              </div>
              {effectiveChatId && (
                <>
                  {showShareButton ? (
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      className={styles.sideButton}
                      onClick={handleShare}
                      style={{ width: 'auto' }}
                    >
                      <IosShare fontSize="small" />
                      {T.translate('generic.share')}
                    </Button>
                  ) : (
                    <div
                      className={`${styles.sideButton} ${styles.shareButtonPlaceholder}`}
                      aria-hidden="true"
                      style={{ width: 'auto' }}
                    >
                      <IosShare fontSize="small" />
                      {T.translate('generic.share')}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {isFileOpen && !fileSidebarAskAI && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                marginBottom: '10px',
              }}
            >
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  className={styles.sideButton}
                  onClick={handleNewSearch}
                  style={{ width: 'auto' }}
                >
                  <Add fontSize="small" />
                  {T.translate('generic.newChat')}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  className={`${styles.sideButton} ${historyMenuOpen ? styles.sideButtonOpen : ''}`}
                  onClick={handleHistoryMenuToggle}
                  style={{ width: 'auto' }}
                >
                  <KeyboardArrowDown
                    fontSize="small"
                    className={`${styles.arrowIcon} ${historyMenuOpen ? styles.arrowIconOpen : ''}`}
                  />
                  {T.translate('case.askAISearchHistory')}
                </Button>
              </div>
              {effectiveChatId && (
                <>
                  {showShareButton ? (
                    <Button
                      type="button"
                      variant="outlined"
                      size="small"
                      className={styles.sideButton}
                      onClick={handleShare}
                      style={{ width: 'auto' }}
                    >
                      <IosShare fontSize="small" />
                      {T.translate('generic.share')}
                    </Button>
                  ) : (
                    <div
                      className={`${styles.sideButton} ${styles.shareButtonPlaceholder}`}
                      aria-hidden="true"
                      style={{ width: 'auto' }}
                    >
                      <IosShare fontSize="small" />
                      {T.translate('generic.share')}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {fileSidebarAskAI && !hidePromptComposer && (
            <div className={styles.historyToggleRow}>
              <Tooltip title={T.translate('generic.newChat') || 'New chat'} placement="top" arrow>
                <button
                  type="button"
                  aria-label={T.translate('generic.newChat') || 'New chat'}
                  className={styles.iconButton}
                  onClick={handleNewSearch}
                >
                  <PostAdd fontSize="small" />
                </button>
              </Tooltip>
              {askAiSearchType !== 'compareFiles' && askAiSearchType !== 'multiFiles' && (
                <Tooltip
                  title={T.translate('case.searchHistory') || 'Search history'}
                  placement="top"
                  arrow
                >
                  <button
                    type="button"
                    aria-label={T.translate('case.askAISearchHistory') || 'Search history'}
                    aria-expanded={historyMenuOpen}
                    aria-haspopup="menu"
                    className={styles.iconButton}
                    onClick={handleHistoryMenuToggle}
                  >
                    <ManageHistory fontSize="small" />
                  </button>
                </Tooltip>
              )}
            </div>
          )}

          <ChatStream
            fetchPending={Boolean(fetchAskAiSseChatMessagesPending)}
            showResponseArea={showResponseArea}
            safeHistory={safeHistory}
            askAiSseCurrentId={askAiSseCurrentId}
            askAiSseHasFirstChunk={Boolean(askAiSseHasFirstChunk)}
            askAiSseProgressMessage={askAiSseProgressMessage}
            askAiSseAssistantText={askAiSseAssistantText}
            askAiSseError={askAiSseError}
            askAiSseStatus={askAiSseStatus}
            askAiSseCitationsBySource={askAiSseCitationsBySource}
            fileIdToName={fileIdToName}
            caseId={caseId}
            onCitationClick={handleCitationClick}
            onRestartPrompt={handleRestartChat}
            streamEndRef={streamEndRef}
            scrollContainerRef={scrollContainerRef}
            onScroll={handleScroll}
            showJumpToLatest={showJumpToLatest}
            onJumpToLatest={() => jumpToLatest('smooth')}
          />

          {!hidePromptComposer && (
            <PromptComposer
              fileSidebarAskAI={fileSidebarAskAI}
              showResponseArea={showResponseArea}
              isLoading={showPromptLoader}
              inputValue={inputValue}
              setInputValue={handleInputValueChange}
              inputRef={inputRef}
              onSend={sendMessage}
              onSubmit={handleSubmit}
              isStreaming={isStreaming}
              onStopStreaming={handleStopStreaming}
              showQuickActionsMenuTrigger={!shouldHideQuickActionsMenuTrigger}
              menuOpen={menuOpen}
              onMenuToggle={handleMenuToggle}
              selectedOutputFormat={selectedOutputFormat}
              outputFormatLabel={
                selectedOutputFormat ? getOutputFormatLabel(selectedOutputFormat) : ''
              }
              onClearOutputFormat={handleClearOutputFormat}
              selectedFilesCount={selectedFiles.length + selectedBundles.length}
              documentsChipLabel={getDocumentsChipLabel}
              onOpenDocuments={handleOpenDocumentsDialog}
              onClearSelectedFiles={handleClearSelectedFiles}
              selectedBundlesCount={0}
              bundlesChipLabel={''}
              bundlesAnchorRef={bundlesAnchorRef}
              onOpenBundlesMenu={() => {}}
              onClearSelectedBundles={() => {}}
              onOpenPromptDialog={() => setAiToolsDialogOpen(true)}
              compareFilesCount={compareFiles.length}
              compareChipLabel={getCompareChipLabel}
              onOpenCompare={handleOpenCompareDialog}
              onClearCompareFiles={handleClearCompareFiles}
              {...(supportsResponseMode
                ? { responseMode, onResponseModeChange: setResponseMode }
                : {})}
            />
          )}

          <Menu
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={() => {
              if (isSavingPrompt) return;
              handleMenuClose();
            }}
            anchorOrigin={
              showResponseArea
                ? { vertical: 'top', horizontal: 'left' }
                : { vertical: 'bottom', horizontal: 'left' }
            }
            transformOrigin={
              showResponseArea
                ? { vertical: 'bottom', horizontal: 'left' }
                : { vertical: 'top', horizontal: 'left' }
            }
            MenuListProps={{
              dense: true,
            }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 200,
                  fontSize: '1rem',
                  boxShadow: '0 16px 44px rgba(15, 23, 42, 0.14)',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                  padding: '8px',
                  marginTop: showResponseArea ? '-10px' : '2px',
                  marginLeft: '-20px',
                },
              },
            }}
          >
            {visibleQuickActionMenuItems
              .filter((menuItem) => menuItem.key !== 'savePrompt')
              .map((menuItem) => (
                <MenuItem
                  key={menuItem.key}
                  disabled={isSavingPrompt}
                  onClick={(event) => {
                    if (menuItem.key === 'options') {
                      handleOptionsOpen(event);
                      return;
                    }
                    if (menuItem.key === 'compare') {
                      handleOpenCompareDialog();
                      handleMenuClose();
                      return;
                    }
                    handleMenuClose();
                  }}
                  onMouseEnter={handleNonAnswerMenuEnter}
                  sx={{ gap: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <menuItem.Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={T.translate(menuItem.translationKey)}
                    primaryTypographyProps={{ fontSize: '1rem' }}
                  />
                </MenuItem>
              ))}
            {(visibleQuickActionMenuItems.length > 0 || savePromptMenuItem) && (
              <Divider sx={{ my: 0.5 }} />
            )}
            {savePromptMenuItem ? (
              <MenuItem
                disabled={isSavingPrompt || !inputValue.trim()}
                onClick={handleSavePrompt}
                onMouseEnter={handleNonAnswerMenuEnter}
                sx={{ gap: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {isSavingPrompt ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <savePromptMenuItem.Icon fontSize="small" />
                      <CircularProgress size={16} />
                    </span>
                  ) : (
                    <savePromptMenuItem.Icon fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={T.translate(savePromptMenuItem.translationKey)}
                  primaryTypographyProps={{ fontSize: '1rem' }}
                />
              </MenuItem>
            ) : null}
            <MenuItem
              disabled={isImprovingPrompt || !inputValue.trim()}
              onClick={handleEnhancePrompt}
              onMouseEnter={handleNonAnswerMenuEnter}
              sx={{ gap: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {isImprovingPrompt ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <AutoAwesome fontSize="small" />
                    <CircularProgress size={16} />
                  </span>
                ) : (
                  <AutoAwesome fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={T.translate('generic.improvePrompt')}
                primaryTypographyProps={{ fontSize: '1rem' }}
              />
            </MenuItem>
            {resetPromptOriginalText ? (
              <MenuItem
                onClick={handleResetEnhancedPrompt}
                onMouseEnter={handleNonAnswerMenuEnter}
                sx={{ gap: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Undo fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={T.translate('generic.resetToOriginal')}
                  primaryTypographyProps={{ fontSize: '1rem' }}
                />
              </MenuItem>
            ) : null}
            {hasSavedStandingInstructions && (
              <>
                <MenuItem
                  onClick={() =>
                    handleStandingInstructionsToggle(!useStandingInstructionsForPrompt)
                  }
                  onMouseEnter={handleNonAnswerMenuEnter}
                  sx={{ gap: 1 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <IntegrationInstructions fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={T.translate('generic.useStandingInstructions')}
                    primaryTypographyProps={{ fontSize: '1rem' }}
                  />
                  <Switch
                    size="small"
                    checked={useStandingInstructionsForPrompt}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(_event, checked) => handleStandingInstructionsToggle(checked)}
                    inputProps={{
                      'aria-label': T.translate('generic.useStandingInstructions') as string,
                    }}
                  />
                </MenuItem>
              </>
            )}
          </Menu>
        </div>

        {Boolean(searchOptionsAnchorEl) && (
          <SearchOptions
            open={Boolean(searchOptionsAnchorEl)}
            anchorEl={searchOptionsAnchorEl}
            versionOptionsVariant="askAiSse"
            // AskAISSE does not use SearchCenter's direct-answers/retrieval toggles; we always show
            // version + prompt placeholders in this variant.
            directAnswersForChatEnabled
            handleClose={() => setSearchOptionsAnchorEl(null)}
          />
        )}

        {!isFileOpen && !fileSidebarAskAI && (
          <div className={styles.sideColumn}>
            {effectiveChatId && (
              <>
                {showShareButton ? (
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    className={styles.sideButton}
                    onClick={handleShare}
                  >
                    <IosShare fontSize="small" />
                    {T.translate('generic.share')}
                  </Button>
                ) : (
                  <div
                    className={`${styles.sideButton} ${styles.shareButtonPlaceholder}`}
                    aria-hidden="true"
                  >
                    <IosShare fontSize="small" />
                    {T.translate('generic.share')}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {answerFormatAnchor && (
          <OutputFormatMenu
            anchorEl={answerFormatAnchor}
            open={answerFormatMenuOpen}
            onClose={handleAnswerFormatMenuClose}
            onSelect={handleAnswerFormatSelect}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            onMouseEnter={handleOutputFormatMenuEnter}
            onMouseLeave={handleOutputFormatMenuLeave}
          />
        )}

        <AiToolsDialog
          open={aiToolsDialogOpen}
          filters={aiToolsFilters}
          onClose={handleAiToolsClose}
          onCategoriesLoaded={handlePromptCategoriesLoaded}
          showStandingInstructionsTab
          onSaveSelections={(data) => {
            const promptText = data?.prompt?.prompt;
            if (typeof promptText === 'string') {
              setInputValue(promptText);
              setResetPromptOriginalText('');
            }
            if (data?.prompt) {
              setSelectedPromptCategoryName(data.prompt.category || undefined);
              setSelectedPromptName(data.prompt.name || undefined);
            }
            // Check if the selected prompt is a comparison prompt
            const isComparisonPrompt = !!data?.prompt?.filterType?.isComparison;
            if (Array.isArray(data?.files) && data.files.length > 0) {
              if (isComparisonPrompt) {
                // Comparison prompt: set compare files, clear documents
                setCompareFiles(data.files as FileItem[]);
                setSelectedFiles([]);
              } else {
                // Non-comparison prompt: set documents, clear compare files
                setSelectedFiles(data.files as FileItem[]);
                setCompareFiles([]);
              }
            }
            if (Array.isArray(data?.location)) {
              if (data.location.length === 0) {
                clearBundleSelection();
              } else {
                clearAskAiBundleParam();
                dispatch(setAskAiSseSelectedBundles(data.location as any[]));
              }
            }
            handleAiToolsClose();
          }}
        />

        {/* Combined files + bundles dialog for non-compare mode */}
        {!compareFilesMode && (
          <FilesAndBundlesSelectionDialog
            open={filesDialogOpen && !compareFilesMode}
            onClose={handleFilesDialogClose}
            onSave={handleFilesAndBundlesSave}
            initialSelectedFiles={selectedFiles}
            initialSelectedBundles={selectedBundles}
          />
        )}

        {/* Files-only dialog for compare mode */}
        {compareFilesMode && (
          <FilesAndBundlesSelectionDialog
            open={filesDialogOpen && compareFilesMode}
            onClose={handleFilesDialogClose}
            onSave={({ files }) => {
              setCompareFiles(files);
              setSelectedFiles([]);
              handleFilesDialogClose();
            }}
            initialSelectedFiles={compareFiles}
            initialSelectedBundles={[]}
            allowedMode="files"
            compareMode
            lockedAnchorFile={
              fileSidebarAskAI && focusFileId
                ? { id: String(focusFileId), name: fileData?.name || String(focusFileId) }
                : null
            }
          />
        )}
      </div>
    </div>
  );
}
